import { useState } from 'react';
import styled from 'styled-components';
import { Modal as SemanticModal } from 'semantic-ui-react';
import PredictionFrequencyPopup from './PredictionFrequencyPopup';
import { formatNumberWithComma } from '../../../utils/formatText';
import { IParsedChartDataPrediction } from '../interfaces';
import { theme } from '../../../styles/theme';
import { getFloatingHostElement } from '../../FloatingManager';

interface IProps {
  chartData: IParsedChartDataPrediction['data'];
  event: string;
  rowIndex: number;
  analysisId: string;
}

const cellColors = ['#e5eafc', '#b2c0f8', '#7f96f4', '#4c6cf0', '#1942ec'];

const CellConlumn = styled(
  ({ borderColor, color, backgroundColor, isLast, isDisabled, ...props }) => (
    <div {...props} />
  ),
)`
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
  width: 95px;
  min-width: 95px;
  height: 48px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  display: grid;
  color: ${({ color }) => color};
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: 4px 8px;
  border-right: ${({ borderColor, isLast }) =>
    !isLast && `1px solid ${borderColor}`};
  border-bottom: ${({ borderColor }) => `1px solid ${borderColor}`};
`;

export default ({ chartData, event, rowIndex, analysisId }: IProps) => {
  const currentRow = chartData.correlationData[event];
  const currentKind = chartData.selectedValue;
  const [selectedCell, setSelectedCell] = useState('');
  const findMaxDifference = (rows: any) => {
    let maxCorrelationValue = Number.MIN_VALUE;
    for (const dayEvent of rows) {
      maxCorrelationValue =
        maxCorrelationValue < dayEvent.strongestValues.correlation
          ? dayEvent.strongestValues.correlation
          : maxCorrelationValue;
    }

    let minCorrelationValue = Number.MAX_VALUE;
    for (const dayEvent of rows) {
      minCorrelationValue =
        minCorrelationValue > dayEvent.strongestValues.correlation
          ? dayEvent.strongestValues.correlation
          : minCorrelationValue;
    }
    if (minCorrelationValue >= 0) {
      return maxCorrelationValue;
    }

    return maxCorrelationValue - minCorrelationValue;
  };

  const getFixedNumber = (value: number) => {
    if (value === 0) {
      return 0;
    } else if (!value) {
      return 'N/A';
    } else {
      return value.toFixed(4);
    }
  };

  const computeCellColor = (maxDiff: any, cellValue: any) => {
    if (currentKind !== 'correlation') {
      return '#fff';
    }
    if (cellValue === 0 || maxDiff === 0 || !cellValue) {
      return cellColors[0];
    }

    return cellColors[Math.ceil(Math.abs(cellValue / (maxDiff / 5))) - 1];
  };
  const maxDiffCorrelation = findMaxDifference(currentRow);

  return (
    <div style={{ display: 'flex' }}>
      {currentRow.map((cellData: any, cellIndex: number) => {
        const backgroundColor = computeCellColor(
          maxDiffCorrelation,
          cellData.strongestValues.correlation,
        );
        const displayValue: any = getFixedNumber(
          cellData.strongestValues[currentKind],
        );
        const cellId = `PredictionCell-${rowIndex}-${cellIndex}`;
        return (
          <SemanticModal
            content={
              <PredictionFrequencyPopup
                analysisId={analysisId}
                eventName={event}
                data={cellData}
                cellId={cellId}
              />
            }
            closeIcon
            onOpen={() => setSelectedCell(cellId)}
            onClose={() => setSelectedCell('')}
            className={'PredictionPopper'}
            key={`cellData-row_${rowIndex}-${cellIndex}`}
            position={'bottom center'}
            disabled={isNaN(displayValue)}
            on={['click']}
            trigger={
              <div id={cellId}>
                <CellConlumn
                  backgroundColor={
                    selectedCell === cellId ? '#ffb931' : backgroundColor
                  }
                  isLast={cellIndex === currentRow.length - 1}
                  isDisabled={isNaN(displayValue)}
                  borderColor={
                    backgroundColor === '#fff'
                      ? theme.colors.secondary400
                      : '#fff'
                  }
                  color={
                    selectedCell !== cellId &&
                    (backgroundColor === cellColors[0] ||
                      backgroundColor === '#fff')
                      ? '#8a94bd'
                      : '#fff'
                  }
                >
                  <span> {formatNumberWithComma(displayValue)}</span>
                  <span style={{ fontSize: '10px' }}>
                    (&#x2265;{`${cellData.strongestFrequencyIndex + 1})`}
                  </span>
                </CellConlumn>
              </div>
            }
            mountNode={getFloatingHostElement()}
          />
        );
      })}
    </div>
  );
};
